import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { InvalidTenantModal } from '../components/invalidTenantModal/InvalidTenantModal'
import { WorkspaceSelector } from '../components/workspaceSelector/WorkspaceSelector'
import { useRevalidateWorkspace } from '../hooks/useRevalidateWorkspace'
import { useNavigationParams } from '../system/useNavigationParams'
import {
  useWorkspaceNavigation,
  type WorkspaceSwitchOptions,
} from '../system/useWorkspaceNavigation'
import { isEqualBranch } from '../uiModels/branches/helpers'
import { useIsPersonaEditor } from '../uiModels/useIsPersonaEditor'
import type { WorkspaceCreateOrUpdateInput } from '../uiModels/workspace/types'
import { useConfigurationComponentWorkspaces } from '../uiModels/workspace/useConfigurationComponentWorkspaces'
import { isEqualWorkspace, workspaceKeyFromConciseWorkspaces } from './helpers'
import { Provider } from './workspaceManagerContext'
import { WorkspaceValidator } from './WorkspaceValidator'

type Props = {
  children: (workspaceKey: Optional<WorkspaceKey>) => ReactNode
}

const WorkspaceManagerProvider = ({ children }: Props) => {
  const { conciseWorkspaces, add, remove, update } =
    useConfigurationComponentWorkspaces()
  const { branch, server, workspaceName } = useNavigationParams()
  const currentWorkspaceKey = workspaceKeyFromConciseWorkspaces({
    workspaceName,
    branch,
    conciseWorkspaces,
    server,
  })

  const getWorkspace = useCallback(
    (key: Optional<WorkspaceKey>) =>
      key
        ? conciseWorkspaces[key.branchKey.server]?.find(
            ({ name }) => name === key.name,
          )
        : undefined,
    [conciseWorkspaces],
  )

  /**
   * True if the workspace manager is locked to an environment (server and tenant). Required if the workspace manager is opened while creating a draft.
   */
  const [lockedToBaseBranch, setLockedToBaseBranch] = useState<BranchKey>()
  const revalidateWorkspace = useRevalidateWorkspace(getWorkspace)
  const navigateToWorkspace = useWorkspaceNavigation()
  const isPersonaEditor = useIsPersonaEditor()

  useEffect(() => {
    if (currentWorkspaceKey && !isPersonaEditor) {
      void navigateToWorkspace(undefined, { mode: 'preserve' })
    }
  }, [currentWorkspaceKey, isPersonaEditor, navigateToWorkspace])

  const handleSwitchWorkspace = useCallback(
    (
      nextWorkspace: Optional<WorkspaceKey>,
      options?: WorkspaceSwitchOptions,
    ) => {
      const { mode, ...navigateOptions } = options ?? {}

      return navigateToWorkspace(nextWorkspace, {
        mode:
          mode ?? (lockedToBaseBranch || !nextWorkspace ? 'preserve' : 'reset'),
        ...navigateOptions,
      })
    },
    [lockedToBaseBranch, navigateToWorkspace],
  )

  const handleDeleteWorkspace = useCallback(
    async (deleteKey: WorkspaceKey) => {
      if (isEqualWorkspace(currentWorkspaceKey, deleteKey)) {
        await navigateToWorkspace(undefined, { mode: 'reset' })
      }
      await remove(deleteKey)
    },
    [currentWorkspaceKey, navigateToWorkspace, remove],
  )

  const handleOpenWorkspaceSelector = (lockToBranch: BranchKey) => {
    setLockedToBaseBranch(lockToBranch)
  }

  const handleCloseWorkspaceSelector = () => {
    setLockedToBaseBranch(undefined)
  }

  const handleUpdateWorkspace = async (
    nextWorkspace: WorkspaceCreateOrUpdateInput,
    shouldUpdateCache?: boolean,
  ) => {
    await update(nextWorkspace, shouldUpdateCache)

    if (
      currentWorkspaceKey &&
      !isEqualBranch(currentWorkspaceKey.branchKey, nextWorkspace.branchKey)
    ) {
      await navigateToWorkspace(
        {
          name: nextWorkspace.name,
          branchKey: nextWorkspace.branchKey,
        },
        { mode: 'preserve' },
      )
    }
  }

  const handleAddWorkspace = async (
    input: WorkspaceCreateOrUpdateInput,
    shouldUpdateCache?: boolean,
  ) => await add(input, shouldUpdateCache)

  const handleSelectWorkspace = async (workspaceKey: WorkspaceKey) => {
    setLockedToBaseBranch(undefined)
    await handleSwitchWorkspace(workspaceKey)
  }

  return (
    <Provider
      value={{
        conciseWorkspaces,
        currentWorkspaceKey,
        getWorkspace,
        onSwitch: handleSwitchWorkspace,
        onCreate: handleAddWorkspace,
        onDelete: handleDeleteWorkspace,
        onUpdate: handleUpdateWorkspace,
        onOpenWorkspaceSelector: handleOpenWorkspaceSelector,
        revalidateWorkspace,
      }}
    >
      {lockedToBaseBranch && (
        <WorkspaceSelector
          isOpen
          onCancel={handleCloseWorkspaceSelector}
          onSelected={handleSelectWorkspace}
          baseBranch={lockedToBaseBranch}
        />
      )}
      <WorkspaceValidator />
      <InvalidTenantModal />
      {children(currentWorkspaceKey)}
    </Provider>
  )
}

export { WorkspaceManagerProvider }
